/* eslint-disable */
import { Tag } from 'antd';
import jsPDF from 'jspdf';
import moment from 'moment';
import * as XLSX from 'xlsx';
import {
  DEFAULT_ACCOUNTING_STATUS,
  FEE_TYPES,
  formatMoney,
  isCourierFeeType,
  isRefundFeeType,
  VOUCHER_STATUS_COLORS,
} from '../../constants/paymentVoucher';
import { title } from 'process';
import callApi from '../../Api/config';
import { PAYMENT_VOUCHER } from '../../constants/EntityName';
import { Role } from '../../constants/AppConstant';
import { getRole } from '../../service/CookieManager';

export const displayText = (value: any) =>
  value !== null && value !== undefined && value !== '' ? value : '—';

export const displayDate = (value: any, withTime = false) =>
  value ? moment(value).format(withTime ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY') : '—';

/**
 * Date a voucher reached its current status — settlement date when Settled,
 * cancellation date when Cancelled, last payment date when Partially Paid,
 * creation date when Generated. Reports filter and display on this date.
 */
export const voucherStatusDate = (row: any) => row?.statusDate || row?.createdAt || null;

export interface VoucherFetchResult {
  ok: boolean;
  rows: any[];
  totalCount: number;
  truncated: boolean;
  message?: string;
}

/**
 * Pull every voucher matching the report filters. The list endpoint caps
 * `limit` at 100, so a single request only ever returns the newest page —
 * reports must page through the whole filtered set or the download is partial.
 * The date range is sent to the server and applied to the voucher status date.
 */
export const fetchAllVouchers = async (filters: {
  status?: string;
  paymentMethod?: string;
  accountingStatus?: string;
  range?: any;
  search?: string;
  pageSize?: number;
  maxPages?: number;
}): Promise<VoucherFetchResult> => {
  const pageSize = filters.pageSize || 100;
  const maxPages = filters.maxPages || 50;
  const [start, end] = filters.range || [];
  const query = {
    limit: pageSize,
    status: filters.status && filters.status !== 'All' ? filters.status : undefined,
    paymentMethod:
      filters.paymentMethod && filters.paymentMethod !== 'All' ? filters.paymentMethod : undefined,
    accountingStatus:
      filters.accountingStatus && filters.accountingStatus !== 'All'
        ? filters.accountingStatus
        : undefined,
    search: filters.search?.trim() || undefined,
    startDate: start ? moment(start).format('YYYY-MM-DD') : undefined,
    endDate: end ? moment(end).format('YYYY-MM-DD') : undefined,
  };

  const rows: any[] = [];
  let totalCount = 0;
  let page = 1;

  while (page <= maxPages) {
    const res = await callApi(PAYMENT_VOUCHER, 'GET', null, { ...query, page });
    if (!res?.status || !res?.data?.data) {
      return {
        ok: false,
        rows: [],
        totalCount: 0,
        truncated: false,
        message: res?.data?.message,
      };
    }
    const batch = res.data.data.vouchers || [];
    totalCount = Number(res.data.data.totalCount || 0);
    rows.push(...batch);
    if (batch.length < pageSize || rows.length >= totalCount) break;
    page += 1;
  }

  return {
    ok: true,
    rows,
    totalCount: totalCount || rows.length,
    truncated: totalCount > rows.length,
  };
};

export const statusTag = (status?: string) =>
  status ? <Tag color={VOUCHER_STATUS_COLORS[status] || 'default'}>{status}</Tag> : '—';

export const accountingStatusValue = (row: any) =>
  row?.accountingStatus || DEFAULT_ACCOUNTING_STATUS;

/** Accounting status is editable by Accountant / Super Admin only. */
export const canUpdateAccountingStatus = () => {
  const role = getRole();

  return role === Role.ACCOUNTANT || role === Role.SUPER_ADMIN;
};

/**
 * Persist a new accounting status. Returns the refreshed voucher, or null when
 * the call failed (the caller surfaces the message).
 */
export const saveAccountingStatus = async (
  voucherId: string,
  accountingStatus: string
): Promise<{ ok: boolean; voucher?: any; message?: string }> => {
  const id = String(voucherId || '').trim();
  if (!id || id === 'undefined') {
    return { ok: false, message: 'Voucher not found' };
  }

  const res = await callApi(`${PAYMENT_VOUCHER}/${id}/accounting-status`, 'PUT', {
    accountingStatus,
  });
  if (!res?.status || !res?.data?.data) {
    return { ok: false, message: res?.data?.message };
  }

  return { ok: true, voucher: res.data.data };
};

export const money = (value: any, currency?: string) => formatMoney(value, currency || 'USD');

const text = (value: any) => String(value ?? '—');

const ensureSpace = (doc: jsPDF, y: number, needed = 40) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - 40) {
    doc.addPage();
    return 40;
  }
  return y;
};

const drawSectionTitle = (doc: jsPDF, title: string, y: number) => {
  y = ensureSpace(doc, y, 28);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, 40, y);
  return y + 16;
};

const drawKeyValue = (doc: jsPDF, label: string, value: string, y: number) => {
  y = ensureSpace(doc, y, 16);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`${label}:`, 40, y);
  doc.setFont('helvetica', 'normal');
  doc.text(value, 160, y);
  return y + 14;
};

const drawEmptyRow = (doc: jsPDF, startY: number) => {
  const y = ensureSpace(doc, startY, 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('No entries recorded.', 40, y);

  return y + 16;
};

const drawSimpleTable = (
  doc: jsPDF,
  headers: string[],
  rows: string[][],
  colWidths: number[],
  startY: number
) => {
  let y = startY;
  const startX = 40;
  const lineHeight = 14;

  const drawHeader = () => {
    y = ensureSpace(doc, y, 24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    let x = startX;
    headers.forEach((header, index) => {
      doc.text(header, x, y);
      x += colWidths[index];
    });
    y += 4;
    doc.setLineWidth(0.4);
    doc.line(startX, y, startX + colWidths.reduce((a, b) => a + b, 0), y);
    y += 12;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach(row => {
    if (y > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      y = 40;
      drawHeader();
    }
    let x = startX;
    row.forEach((cell, index) => {
      const maxWidth = colWidths[index] - 4;
      const value = text(cell);
      const truncated =
        doc.getTextWidth(value) > maxWidth
          ? `${value.slice(0, Math.max(3, Math.floor((maxWidth / Math.max(doc.getTextWidth(value), 1)) * value.length) - 1))}…`
          : value;
      doc.text(truncated, x, y);
      x += colWidths[index];
    });
    y += lineHeight;
  });

  return y + 8;
};

export const downloadVoucherReport = (voucher: any) => {
  if (!voucher?.voucherId && !voucher?.id) {
    return;
  }

  const currency = voucher.currency || 'USD';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  let y = 40;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(`Payment Voucher ${text(voucher.voucherId)}`, 40, y);
  y += 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Status: ${text(voucher.status)}`, 40, y);
  y += 24;

  y = drawSectionTitle(doc, 'Order Information', y);
  y = drawKeyValue(doc, 'Order ID', text(voucher.displayOrderId || voucher.orderId), y);
  y = drawKeyValue(doc, 'Customer', text(voucher.customerName), y);
  y = drawKeyValue(doc, 'Service', text(voucher.serviceName), y);
  y = drawKeyValue(doc, 'Members', String((voucher.members || []).length), y);
  y += 8;

  y = drawSectionTitle(doc, 'Payment Received', y);
  y = drawKeyValue(
    doc,
    'Total Amount',
    `${currency} ${money(voucher.receivedAmount, currency)}`,
    y
  );
  y = drawKeyValue(doc, 'Entries', String((voucher.receipts || []).length || 1), y);
  y = drawKeyValue(
    doc,
    'Latest Method',
    voucher.bankAccount
      ? `${text(voucher.paymentMethod)} — ${voucher.bankAccount}`
      : text(voucher.paymentMethod),
    y
  );
  y = drawKeyValue(doc, 'Latest Date', displayDate(voucher.receivedDate), y);
  y = drawKeyValue(doc, 'Transaction ID', text(voucher.transactionId), y);
  y = drawKeyValue(doc, 'Recorded By', text(voucher.createdBy), y);
  y = drawKeyValue(doc, 'Created Date', displayDate(voucher.createdAt, true), y);
  y += 8;

  if ((voucher.receipts || []).length) {
    y = drawSectionTitle(doc, 'Receipt Entries', y);
    y = drawSimpleTable(
      doc,
      ['#', 'Amount', 'Date', 'Method', 'Sender', 'Txn ID', 'Recorded By'],
      (voucher.receipts || []).map((row: any, index: number) => [
        String(index + 1),
        money(row.amount, currency),
        displayDate(row.receivedDate),
        row.bankAccount ? `${row.paymentMethod} — ${row.bankAccount}` : text(row.paymentMethod),
        text(row.senderName),
        text(row.transactionId),
        text(row.addedBy),
      ]),
      [24, 60, 60, 100, 70, 80, 70],
      y
    );
  }

  y = drawSectionTitle(doc, 'Financial Summary', y);
  const summaryRows: string[][] = [
    ['Total Received Amount', money(voucher.receivedAmount, currency)],
    ...(FEE_TYPES || [])
      .filter((feeType: string) => Number(voucher.feeTotals?.[feeType] || 0) > 0.004)
      .map((feeType: string) => [feeType, money(voucher.feeTotals?.[feeType] || 0, currency)]),
    ...(Number(voucher.withdrawnAmount || 0) > 0.004
      ? [['Total Withdrawn', money(voucher.withdrawnAmount, currency)]]
      : []),
    ...(Number(voucher.settledAmount || 0) > 0.004
      ? [['Settled Amount', money(voucher.settledAmount, currency)]]
      : []),
    ['Remaining Amount', money(voucher.remainingAmount, currency)],
  ];
  y = drawSimpleTable(doc, ['Particulars', 'Amount'], summaryRows, [280, 120], y);

  if ((voucher.withdrawals || []).length) {
    y = drawSectionTitle(doc, 'Withdrawals', y);
    y = drawSimpleTable(
      doc,
      ['Member', 'Fee Type', 'Amount', 'Date', 'Mode', 'Govt Ref', 'Recorded By'],
      (voucher.withdrawals || []).map((row: any) => [
        text(row.memberName),
        text(row.feeType),
        money(row.amount, currency),
        displayDate(row.paymentDate),
        row.paymentAccount ? `${row.paymentMode} — ${row.paymentAccount}` : text(row.paymentMode),
        text(row.govtReference),
        text(row.paidBy),
      ]),
      [80, 80, 60, 60, 90, 70, 70],
      y
    );
  }

  doc.save(`${voucher.voucherId || 'payment-voucher'}.pdf`);
};

export const downloadVoucherListPdf = (vouchers: any[], fileName?: string) => {
  if (!vouchers?.length) {
    return;
  }

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const headers = [
    'Voucher ID',
    'Order ID',
    'Customer',
    'Received',
    'Govt Fee',
    'Courier Fee',
    'Settled',
    'Status',
    'Status Date',
    'Created',
  ];
  const colWidths = [80, 80, 110, 65, 65, 65, 65, 70, 76, 86];
  let y = 40;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Payment Voucher Report', 40, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    `Generated: ${moment().format('DD-MM-YYYY HH:mm')}  |  Records: ${vouchers.length}`,
    40,
    y
  );
  y += 20;

  const rows = vouchers.map((row: any) => [
    text(row.displayVoucherId || row.voucherId),
    text(row.displayOrderId || row.orderId),
    text(row.customerDisplayName || row.customerName),
    money(row.receivedAmount, row.currency),
    money(row.memberGovtFee ?? row.feeTotals?.['Government Fee'] ?? 0, row.currency),
    money(row.memberCourierFee ?? row.feeTotals?.['Courier Label'] ?? 0, row.currency),
    money(row.settledAmount, row.currency),
    text(row.status),
    displayDate(voucherStatusDate(row)),
    displayDate(row.createdAt, true),
  ]);

  y = drawSimpleTable(doc, headers, rows, colWidths, y);

  // Per-voucher breakdown — every Record Receipt, Member-wise Record Payment
  // and Settlement entry, so the report carries the same detail as the voucher
  // detail screen instead of just the summary line.
  vouchers.forEach((voucher: any) => {
    const currency = voucher.currency || 'USD';
    const receipts = voucher.receipts || [];
    const withdrawals = voucher.withdrawals || [];
    const settlements = voucher.settlements || [];

    doc.addPage();
    y = 40;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(
      `Voucher ${text(voucher.displayVoucherId || voucher.voucherId)}  •  Order ${text(
        voucher.displayOrderId || voucher.orderId
      )}  •  ${text(voucher.customerDisplayName || voucher.customerName)}`,
      40,
      y
    );
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      `Status: ${text(voucher.status)}  |  Status Date: ${displayDate(
        voucherStatusDate(voucher)
      )}  |  Accounting Status: ${accountingStatusValue(voucher)}  |  Created: ${displayDate(
        voucher.createdAt,
        true
      )}  |  Recorded By: ${text(voucher.createdBy)}`,
      40,
      y
    );
    y += 18;

    y = drawSimpleTable(
      doc,
      ['Received', 'Withdrawn', 'Settled', 'Remaining', 'Currency', 'Latest Method'],
      [
        [
          money(voucher.receivedAmount, currency),
          money(voucher.withdrawnAmount, currency),
          money(voucher.settledAmount, currency),
          money(voucher.remainingAmount, currency),
          text(currency),
          voucher.bankAccount
            ? `${text(voucher.paymentMethod)} — ${voucher.bankAccount}`
            : text(voucher.paymentMethod),
        ],
      ],
      [90, 90, 90, 90, 70, 180],
      y
    );

    y = drawSectionTitle(doc, `Payment Received / Record Receipt (${receipts.length})`, y);
    y = receipts.length
      ? drawSimpleTable(
          doc,
          ['#', 'Amount', 'Date', 'Method', 'Sender', 'Txn ID', 'Received From', 'Section', 'By'],
          receipts.map((row: any, index: number) => [
            String(index + 1),
            money(row.amount, row.currency || currency),
            displayDate(row.receivedDate),
            row.bankAccount ? `${row.paymentMethod} — ${row.bankAccount}` : text(row.paymentMethod),
            text(row.senderName),
            text(row.transactionId),
            text(row.receiptSource),
            text(row.feeCategory),
            text(row.addedBy),
          ]),
          [22, 70, 65, 110, 80, 90, 110, 80, 80],
          y
        )
      : drawEmptyRow(doc, y);

    y = drawSectionTitle(doc, `Member-wise Record Payment (${withdrawals.length})`, y);
    y = withdrawals.length
      ? drawSimpleTable(
          doc,
          [
            'Member',
            'Fee Type',
            'Amount',
            'Date',
            'Mode',
            'Govt Ref',
            'Tracking / Refund ID',
            'Portal',
            'Paid By',
            'Remark',
          ],
          withdrawals.map((row: any) => [
            text(row.memberName),
            text(row.feeType),
            money(row.amount, currency),
            displayDate(row.paymentDate),
            row.paymentAccount
              ? `${row.paymentMode} — ${row.paymentAccount}`
              : text(row.paymentMode),
            text(row.govtReference),
            text(isRefundFeeType(row.feeType) ? row.refundId : row.trackingId),
            text(row.portalName),
            text(row.paidBy),
            text(row.remark),
          ]),
          [80, 75, 60, 60, 95, 70, 75, 60, 70, 100],
          y
        )
      : drawEmptyRow(doc, y);

    y = drawSectionTitle(doc, `Settlements (${settlements.length})`, y);
    y = settlements.length
      ? drawSimpleTable(
          doc,
          ['#', 'Amount', 'Settlement Date', 'Note', 'Added By', 'Recorded On'],
          settlements.map((row: any, index: number) => [
            String(index + 1),
            money(row.amount, currency),
            displayDate(row.settlementDate),
            text(row.note),
            text(row.addedBy),
            displayDate(row.createdAt, true),
          ]),
          [22, 80, 100, 230, 110, 110],
          y
        )
      : drawEmptyRow(doc, y);
  });

  doc.save(fileName || `Payment_Voucher_Report_${moment().format('YYYYMMDD')}.pdf`);
};

const num = (value: any) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const joinUnique = (values: any[]) =>
  Array.from(
    new Set(values.filter(value => value !== null && value !== undefined && value !== ''))
  ).join(', ');

const joinDates = (values: any[]) =>
  joinUnique(values.filter(Boolean).map(value => displayDate(value)));

const withdrawalMode = (row: any) =>
  row.paymentAccount ? `${row.paymentMode} — ${row.paymentAccount}` : row.paymentMode || '';

/** Most recent entry by its own date (falls back to when it was recorded). */
const latestEntry = (rows: any[], dateKey: string) =>
  [...rows].sort((a, b) =>
    String(b[dateKey] || b.createdAt || '').localeCompare(String(a[dateKey] || a.createdAt || ''))
  )[0];

/** Columns shared by every detail sheet so each one stands alone when filtered. */
const voucherKeyColumns = (voucher: any) => ({
  'Voucher ID': voucher.displayVoucherId || voucher.voucherId || '',
  'Order ID': voucher.displayOrderId || voucher.orderId || '',
  'Customer Name': voucher.customerDisplayName || voucher.customerName || '',
  Status: voucher.status || '',
  'Status Date': displayDate(voucherStatusDate(voucher)),
  'Accounting Status': accountingStatusValue(voucher),
});

/**
 * Flattens vouchers into one summary row each plus a row per Record Receipt,
 * Member-wise Record Payment and Settlement entry.
 */
export const buildVoucherExportRows = (vouchers: any[]) => {
  const summary: any[] = [];
  const receipts: any[] = [];
  const withdrawals: any[] = [];
  const settlements: any[] = [];

  (vouchers || []).forEach((voucher: any) => {
    const keys = voucherKeyColumns(voucher);
    const currency = voucher.currency || 'USD';
    const voucherReceipts = voucher.receipts || [];
    const voucherWithdrawals = voucher.withdrawals || [];
    const voucherSettlements = voucher.settlements || [];

    const govtRows = voucherWithdrawals.filter((row: any) => row.feeType === 'Government Fee');
    const courierRows = voucherWithdrawals.filter((row: any) => isCourierFeeType(row.feeType));
    const refundRows = voucherWithdrawals.filter((row: any) => isRefundFeeType(row.feeType));
    const feeTotal = (...feeTypes: string[]) =>
      feeTypes.reduce((sum, feeType) => sum + num(voucher.feeTotals?.[feeType] ?? 0), 0);
    // Refund entries carry no payment mode, so take the latest entry that used one.
    const latestPayment = latestEntry(
      voucherWithdrawals.filter((row: any) => row.paymentMode),
      'paymentDate'
    );

    // Column order requested for the Payment Voucher download.
    summary.push({
      'Voucher ID': keys['Voucher ID'],
      Status: keys.Status,
      'Status Date': keys['Status Date'],
      'Accounting Status': keys['Accounting Status'],
      Currency: currency,
      'Receipt Amount': num(voucher.receivedAmount),
      'Latest Receipt Date': voucher.receivedDate ? displayDate(voucher.receivedDate) : '',
      'Receipt Method': voucher.paymentMethod || '',
      'Sender Name': voucher.senderName || '',
      'Government Fee': feeTotal('Government Fee'),
      'Govt Fee Date': joinDates(govtRows.map((row: any) => row.paymentDate)),
      'Govt Payment Method': joinUnique(govtRows.map(withdrawalMode)),
      'Courier Label Cost': feeTotal('Courier Label', 'Courier Fee'),
      'Label Purchase Date': joinDates(courierRows.map((row: any) => row.purchasedDate)),
      'Portal Name': joinUnique(courierRows.map((row: any) => row.portalName)),
      'Refund Amount': feeTotal('Refund'),
      'Refund Date': joinDates(refundRows.map((row: any) => row.paymentDate)),
      'Other Charges': feeTotal('Other Charges'),
      'Remaining Amount': num(voucher.remainingAmount),
      'Settled Amount': num(voucher.settledAmount),
      'Settled Date': joinDates(voucherSettlements.map((row: any) => row.settlementDate)),
      'Order ID': keys['Order ID'],
      'Customer Name': keys['Customer Name'],
      'Service Name': voucher.serviceName || '',
      'Total Members': (voucher.members || []).length,
      'Receipt Entries': voucherReceipts.length,
      'Payment Entries': voucherWithdrawals.length,
      'Settlement Entries': voucherSettlements.length,
      'Latest Payment Method': latestPayment ? withdrawalMode(latestPayment) : '',
      'Bank Account': voucher.bankAccount || '',
      'Latest Transaction ID': voucher.transactionId || '',
      'Accounting Status Updated By': voucher.accountingStatusBy || '',
      'Accounting Status Updated On': voucher.accountingStatusAt
        ? displayDate(voucher.accountingStatusAt, true)
        : '',
      'Recorded By': voucher.createdBy || '',
      'Created Date': displayDate(voucher.createdAt, true),
      'Cancelled By': voucher.cancelledBy || '',
      'Cancelled Date': voucher.cancelledAt ? displayDate(voucher.cancelledAt, true) : '',
    });

    voucherReceipts.forEach((row: any, index: number) => {
      receipts.push({
        ...keys,
        'Receipt #': index + 1,
        Amount: num(row.amount),
        Currency: row.currency || currency,
        'Received Date': displayDate(row.receivedDate),
        'Payment Method': row.paymentMethod || '',
        'Bank Account': row.bankAccount || '',
        'Sender Name': row.senderName || '',
        'Transaction ID': row.transactionId || '',
        'Received From': row.receiptSource || '',
        'Fee Section': row.feeCategory || '',
        'Receipt Image': row.imageUrl || '',
        'Added By': row.addedBy || '',
        'Recorded On': displayDate(row.createdAt, true),
      });
    });

    voucherWithdrawals.forEach((row: any) => {
      withdrawals.push({
        ...keys,
        Member: row.memberName || '',
        'Fee Type': row.feeType || '',
        Amount: num(row.amount),
        Currency: currency,
        'Payment Date': displayDate(row.paymentDate),
        'Payment Mode': row.paymentMode || '',
        'Payment Account': row.paymentAccount || '',
        'Govt Reference': row.govtReference || '',
        'Refund ID': row.refundId || '',
        'Tracking ID': row.trackingId || '',
        'Portal Name': row.portalName || '',
        'Purchased Date': displayDate(row.purchasedDate),
        'Label Cost':
          row.labelCost === null || row.labelCost === undefined ? '' : num(row.labelCost),
        'Received Fee':
          row.receivedFee === null || row.receivedFee === undefined ? '' : num(row.receivedFee),
        'Paid By': row.paidBy || '',
        Remark: row.remark || '',
        'Recorded On': displayDate(row.createdAt, true),
      });
    });

    voucherSettlements.forEach((row: any, index: number) => {
      settlements.push({
        ...keys,
        'Settlement #': index + 1,
        Amount: num(row.amount),
        Currency: currency,
        'Settlement Date': displayDate(row.settlementDate),
        Note: row.note || '',
        'Added By': row.addedBy || '',
        'Recorded On': displayDate(row.createdAt, true),
      });
    });
  });

  return { summary, receipts, withdrawals, settlements };
};

/**
 * Multi-sheet voucher export: summary plus one sheet per entry type, so the
 * download carries every Record Receipt / Record Payment / Settlement detail.
 */
export const downloadVoucherListExcel = (vouchers: any[], fileName?: string) => {
  if (!vouchers?.length) {
    return;
  }

  const { summary, receipts, withdrawals, settlements } = buildVoucherExportRows(vouchers);
  const book = XLSX.utils.book_new();

  const appendSheet = (name: string, rows: any[], emptyMessage: string) => {
    const sheet = rows.length
      ? XLSX.utils.json_to_sheet(rows)
      : XLSX.utils.json_to_sheet([{ Info: emptyMessage }]);
    XLSX.utils.book_append_sheet(book, sheet, name);
  };

  appendSheet('Voucher Summary', summary, 'No vouchers matched the selected filters');
  appendSheet('Payment Received', receipts, 'No receipt entries recorded');
  appendSheet('Record Payment', withdrawals, 'No member payment entries recorded');
  appendSheet('Settlements', settlements, 'No settlement entries recorded');

  XLSX.writeFile(book, fileName || `Payment_Voucher_Report_${moment().format('YYYYMMDD')}.xlsx`);
};

export const withdrawalColumns = (currency: string) => [
  { title: 'Fee Type', dataIndex: 'feeType', key: 'feeType', render: (value: any) => value || '—' },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render: (value: any) => money(value, currency),
  },
  {
    title: 'Date',
    dataIndex: 'paymentDate',
    key: 'paymentDate',
    render: (value: any) => displayDate(value),
  },
  {
    title: 'Payment Mode',
    dataIndex: 'paymentMode',
    key: 'paymentMode',
    render: (_: any, row: any) =>
      `${row.paymentMode || '—'}${row.paymentAccount ? ` — ${row.paymentAccount}` : ''}`,
  },
  {
    title: 'Govt Reference',
    dataIndex: 'govtReference',
    key: 'govtReference',
    render: (value: any) => value || '—',
  },
  {
    title: 'Created On',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (value: any) => displayDate(value, true),
  },
  {
    title: 'Performed By',
    dataIndex: 'performedBy',
    render: (value: any, row: any) =>
      value || row?.paidBy || row?.addedBy || row?.recordedBy || '—',
  },
  {
    title: 'Details',
    key: 'details',
    render: (_: any, row: any) => {
      if (row.feeType === 'Courier Label') {
        return `Tracking: ${row.trackingId || '—'} · ${row.portalName || '—'} · Purchased ${displayDate(
          row.purchasedDate
        )}`;
      }
      if (isRefundFeeType(row.feeType)) {
        return `Refund ID: ${row.refundId || '—'} · Refunded ${displayDate(row.paymentDate)}`;
      }

      return '—';
    },
  },
  { title: 'Remark', dataIndex: 'remark', key: 'remark', render: (value: any) => value || '—' },
];
